// pay `GET /api/v1/metrics/business` 응답의 방어적 파서와 로더. 타입은 businessMetricsTypes.ts.
// 필드마다 한 경로만 읽는다(대체 체인 없음).
import type {
  BusinessGroupBy,
  BusinessFunnelStepKey,
  BusinessRevenueBucket,
  BusinessRevenue,
  BusinessSubscriptions,
  BusinessFunnelStep,
  BusinessDiscounts,
  BusinessReferral,
  BusinessAlerts,
  BusinessMetricsResponse,
} from './businessMetricsTypes';

export type {
  BusinessGroupBy,
  BusinessRevenueSource,
  BusinessFunnelStepKey,
  BusinessAlertLevel,
  BusinessRevenueBucket,
  BusinessRevenueCurrency,
  BusinessRecurringRevenue,
  BusinessRevenue,
  BusinessCohort,
  BusinessSubscriptions,
  BusinessFunnelStep,
  BusinessDiscountCode,
  BusinessDiscounts,
  BusinessReferral,
  BusinessWebhookFailure,
  BusinessAlertItem,
  BusinessAlerts,
  BusinessMetricsResponse,
} from './businessMetricsTypes';

type Rec = Record<string, unknown>;

const asRecord = (value: unknown): Rec | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Rec : null;
const numOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
/** 계약상 항상 오는 숫자. 형식이 깨졌으면 0 — 서버가 비워 보낸 값을 다른 값으로 바꾸지 않는다. */
const num = (value: unknown): number => numOrNull(value) ?? 0;
const strOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const str = (value: unknown): string => strOrNull(value) ?? '';
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const records = (value: unknown): Rec[] => list(value).flatMap((item) => {
  const record = asRecord(item);
  return record ? [record] : [];
});
const strings = (value: unknown): string[] => list(value).filter((item): item is string => typeof item === 'string');
const pick = <T extends string>(value: unknown, allowed: readonly T[]): T | null =>
  (allowed as readonly unknown[]).includes(value) ? value as T : null;

const FUNNEL_KEYS: readonly BusinessFunnelStepKey[] = [
  'pricing_viewed', 'buy_page_opened', 'checkout_created', 'payment_completed', 'retained_14d',
];

const parseBucket = (r: Rec): BusinessRevenueBucket => ({
  period: str(r['period']),
  paymentCount: num(r['paymentCount']),
  chargedTotal: num(r['chargedTotal']),
  refundCount: num(r['refundCount']),
  refundedTotal: num(r['refundedTotal']),
  netRevenue: num(r['netRevenue']),
});

const parseRevenue = (value: unknown): BusinessRevenue | null => {
  const r = asRecord(value);
  const source = pick(r?.['source'], ['PADDLE_LEDGER', 'ORDERS_FIRST_PAYMENT'] as const);
  if (!r || !source) return null;
  return {
    source,
    sourceNote: str(r['sourceNote']),
    currencies: records(r['currencies']).map((c) => ({
      currency: str(c['currency']),
      paymentCount: num(c['paymentCount']),
      chargedTotal: num(c['chargedTotal']),
      tax: numOrNull(c['tax']),
      discount: numOrNull(c['discount']),
      fee: numOrNull(c['fee']),
      earnings: numOrNull(c['earnings']),
      refundCount: num(c['refundCount']),
      refundedTotal: num(c['refundedTotal']),
      netRevenue: num(c['netRevenue']),
      buckets: records(c['buckets']).map(parseBucket),
    })),
    recurring: records(r['recurring']).map((m) => ({
      currency: str(m['currency']),
      monthlyActive: num(m['monthlyActive']),
      yearlyActive: num(m['yearlyActive']),
      mrr: num(m['mrr']),
      arr: num(m['arr']),
      basis: 'LIST_PRICE' as const,
    })),
  };
};

const parseSubscriptions = (value: unknown): BusinessSubscriptions | null => {
  const r = asRecord(value);
  if (!r) return null;
  const retention = asRecord(r['retention14d']);
  return {
    active: num(r['active']),
    paidActive: num(r['paidActive']),
    grantedActive: num(r['grantedActive']),
    monthlyActive: num(r['monthlyActive']),
    yearlyActive: num(r['yearlyActive']),
    newInPeriod: num(r['newInPeriod']),
    canceledInPeriod: num(r['canceledInPeriod']),
    cancelScheduled: num(r['cancelScheduled']),
    expiredInPeriod: num(r['expiredInPeriod']),
    paymentProblem: num(r['paymentProblem']),
    failedPaymentsInPeriod: numOrNull(r['failedPaymentsInPeriod']),
    activeAtStart: num(r['activeAtStart']),
    churnRatePercent: numOrNull(r['churnRatePercent']),
    averageLifetimeDays: numOrNull(r['averageLifetimeDays']),
    endedCount: num(r['endedCount']),
    retention14d: {
      eligible: num(retention?.['eligible']),
      retained: num(retention?.['retained']),
      percent: numOrNull(retention?.['percent']),
    },
    cohorts: records(r['cohorts']).map((c) => ({
      cohortMonth: str(c['cohortMonth']),
      started: num(c['started']),
      retained: list(c['retained']).map(num),
    })),
  };
};

const parseFunnel = (value: unknown): { steps: BusinessFunnelStep[] } | null => {
  const r = asRecord(value);
  if (!r) return null;
  return {
    steps: records(r['steps']).flatMap((s) => {
      const key = pick(s['key'], FUNNEL_KEYS);
      return key ? [{
        key,
        label: str(s['label']),
        count: numOrNull(s['count']),
        collected: s['collected'] === true,
        source: str(s['source']),
        note: strOrNull(s['note']),
      }] : [];
    }),
  };
};

const parseDiscounts = (value: unknown): BusinessDiscounts | null => {
  const r = asRecord(value);
  if (!r) return null;
  const founder = asRecord(r['founder']);
  return {
    catalogStatus: r['catalogStatus'] === 'OK' ? 'OK' : 'UNAVAILABLE',
    catalogMessage: strOrNull(r['catalogMessage']),
    codes: records(r['codes']).map((c) => ({
      discountId: str(c['discountId']),
      code: strOrNull(c['code']),
      status: strOrNull(c['status']),
      usageLimit: numOrNull(c['usageLimit']),
      timesUsed: numOrNull(c['timesUsed']),
      remaining: numOrNull(c['remaining']),
      expiresAt: strOrNull(c['expiresAt']),
      ledgerUses: num(c['ledgerUses']),
      ledgerDiscountTotal: num(c['ledgerDiscountTotal']),
      currency: strOrNull(c['currency']),
      source: strOrNull(c['source']),
      checkoutApplications: num(c['checkoutApplications']),
    })),
    founder: {
      limit: num(founder?.['limit']),
      codes: strings(founder?.['codes']),
      used: numOrNull(founder?.['used']),
      remaining: numOrNull(founder?.['remaining']),
    },
  };
};

const parseReferral = (value: unknown): BusinessReferral => {
  const r = asRecord(value);
  return {
    status: r?.['status'] === 'OK' ? 'OK' : 'NOT_AVAILABLE',
    message: strOrNull(r?.['message']),
    codesIssued: numOrNull(r?.['codesIssued']),
    signups: numOrNull(r?.['signups']),
    confirmed: numOrNull(r?.['confirmed']),
    canceled: numOrNull(r?.['canceled']),
    rewardsPending: numOrNull(r?.['rewardsPending']),
    rewardsPaid: numOrNull(r?.['rewardsPaid']),
  };
};

const parseAlerts = (value: unknown): BusinessAlerts => {
  const r = asRecord(value);
  return {
    webhookFailuresUnresolved: numOrNull(r?.['webhookFailuresUnresolved']),
    webhookFailures24h: numOrNull(r?.['webhookFailures24h']),
    recentWebhookFailures: records(r?.['recentWebhookFailures']).map((f) => ({
      eventId: str(f['eventId']),
      eventType: str(f['eventType']),
      failureCount: num(f['failureCount']),
      lastFailedAt: str(f['lastFailedAt']),
      lastError: strOrNull(f['lastError']),
      resolved: f['resolved'] === true,
    })),
    paymentFailuresToday: numOrNull(r?.['paymentFailuresToday']),
    paymentFailuresAvg7d: numOrNull(r?.['paymentFailuresAvg7d']),
    paymentFailureSpike: r?.['paymentFailureSpike'] === true,
    refundRequestsPending: num(r?.['refundRequestsPending']),
    paddleRefundsPendingApproval: numOrNull(r?.['paddleRefundsPendingApproval']),
    items: records(r?.['items']).map((i) => ({
      level: pick(i['level'], ['critical', 'warning', 'info'] as const) ?? 'info',
      code: str(i['code']),
      message: str(i['message']),
    })),
  };
};

export const normalizeBusinessMetricsResponse = (payload: unknown): BusinessMetricsResponse | null => {
  const r = asRecord(payload);
  const applicationId = strOrNull(r?.['applicationId']);
  if (!r || !applicationId) return null;
  return {
    applicationId,
    timezone: str(r['timezone']),
    startDate: str(r['startDate']),
    endDate: str(r['endDate']),
    groupBy: pick(r['groupBy'], ['DAY', 'WEEK', 'MONTH'] as const) ?? 'DAY',
    generatedAt: str(r['generatedAt']),
    planCodes: strings(r['planCodes']),
    revenue: parseRevenue(r['revenue']),
    subscriptions: parseSubscriptions(r['subscriptions']),
    funnel: parseFunnel(r['funnel']),
    discounts: parseDiscounts(r['discounts']),
    referral: parseReferral(r['referral']),
    alerts: parseAlerts(r['alerts']),
  };
};

export interface BusinessMetricsQuery {
  applicationId: string;
  startDate: string;
  endDate: string;
  groupBy: BusinessGroupBy;
}

export interface BusinessMetricsClient {
  get: (url: string, config: { headers: Record<string, string>; withCredentials: boolean }) => Promise<{ data: unknown }>;
}

export const BUSINESS_METRICS_PATH = '/api/v1/metrics/business';

export const buildBusinessMetricsQuery = (
  applicationId: string, startDate: string, endDate: string, groupBy: BusinessGroupBy,
): BusinessMetricsQuery => ({ applicationId, startDate, endDate, groupBy });

export const buildBusinessMetricsUrl = (query: BusinessMetricsQuery): string =>
  `${BUSINESS_METRICS_PATH}?${new URLSearchParams({ ...query }).toString()}`;

export const fetchBusinessMetrics = async (
  query: BusinessMetricsQuery, headers: Record<string, string>, client: BusinessMetricsClient,
): Promise<BusinessMetricsResponse> => {
  const response = await client.get(buildBusinessMetricsUrl(query), { headers, withCredentials: true });
  const normalized = normalizeBusinessMetricsResponse(response.data);
  if (!normalized) {
    throw new Error('사업 지표 응답을 읽을 수 없습니다.');
  }
  return normalized;
};
